import { AxiosResponse } from 'axios'
import useSetBreadcrumb from 'hooks/useSetBreadcrumb/useSetBreadcrumb'
import { get, isString, isUndefined } from 'lodash-es'
import { useCallback, useState } from 'react'
import { CancelRefreshTokenExpired, UnknownErrorMessage } from 'types/errorText'
import { API_CANCLED } from 'utilities/APIErrorMessage'
import errorMessages from 'utilities/errorMessages'
import Guid from 'utilities/Guid'
import toast from 'utilities/toast/toast'

export enum FetchingStatus {
    IDLE = 'IDLE',
    LOADING = 'LOADING',
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR',
}

export interface IConfig {
    showErrorToast?: boolean
    showSuccessToast?: boolean
    successMessage?: string
    breadcrumbName?: string
}

type TOrderwireService<T extends any[], U> = (
    ...args: T
) => Promise<AxiosResponse<U>>

const UIErrors = ['RangeError']

export const isLoading = (status: FetchingStatus | FetchingStatus[]) => {
    if (Array.isArray(status)) {
        return status.includes(FetchingStatus.LOADING)
    }
    return status === FetchingStatus.LOADING
}

export const isNotReady = (status: FetchingStatus | FetchingStatus[]) => {
    if (Array.isArray(status)) {
        return status.some(
            (s) => FetchingStatus.LOADING === s || FetchingStatus.IDLE === s,
        )
    }
    return status === FetchingStatus.LOADING || status === FetchingStatus.IDLE
}

export const isSuccess = (status: FetchingStatus | FetchingStatus[]) => {
    if (Array.isArray(status)) {
        return status.every((s) => s === FetchingStatus.SUCCESS)
    }
    return status === FetchingStatus.SUCCESS
}
export const isError = (status: FetchingStatus | FetchingStatus[]) => {
    if (Array.isArray(status)) {
        return status.some((s) => s === FetchingStatus.ERROR)
    }
    return status === FetchingStatus.ERROR
}
/**
 *
 * @param apiFunc
 * @param IConfig
 * @returns [Array<data>, fetchFunc(), FetchingStatus, error, setData]
 */
const useFetchApi = <T, F extends any[]>(
    apiFunc: TOrderwireService<F, T>,
    config?: IConfig,
) => {
    const [statusFetching, setFetchingStatus] = useState<FetchingStatus>(
        FetchingStatus.IDLE,
    )
    const setBreadcrumb = useSetBreadcrumb()
    const [response, setResponse] = useState<T | undefined>()
    /**
     * TODO: get error response if need
     */
    const [error, setError] = useState<any>()

    const callApi = useCallback(
        async (...hasParams: F) => {
            try {
                setError(undefined) // Reset state of error
                setResponse(undefined) // Reset state for response output.
                setFetchingStatus(FetchingStatus.LOADING)
                const result = await apiFunc(...hasParams)

                setResponse(result.data)

                setFetchingStatus(FetchingStatus.SUCCESS)

                if (hasParams.length >= 1) {
                    const guidParams = hasParams.find(
                        (i: any) => isString(i) && Guid.isValid(i),
                    )

                    if (!isUndefined(guidParams)) {
                        const value = get(
                            result.data,
                            config?.breadcrumbName ?? 'name',
                        )

                        if (!isUndefined(value)) {
                            setBreadcrumb({
                                key: guidParams,
                                value,
                            })
                        }
                    }
                }

                if (config?.showSuccessToast) {
                    toast.success(config.successMessage ?? '')
                }
                return result.data
            } catch (error) {
                let {
                    name,
                    message: errorMessageRes,
                    arg,
                    isHttpError,
                } = errorMessages(error)

                // to avoid rethrow error in some Error related to UI
                // For example, the error may occur in the service layer (e.g. calling toISODateString() the date formatter function with invalid date)
                // In this case, instead showing the toast error and rethrow the error, just set an Error and change fetching status
                if (name && UIErrors.includes(name) && !isHttpError) {
                    setError(error)
                    setFetchingStatus(FetchingStatus.ERROR)
                    console.log(`[useFetchApi] ${name}: ${errorMessageRes}`)
                    return
                }

                if (errorMessageRes === API_CANCLED) {
                    setFetchingStatus(FetchingStatus.SUCCESS)
                    return
                }

                // Other errors
                if (
                    config?.showErrorToast &&
                    errorMessageRes !== CancelRefreshTokenExpired &&
                    errorMessageRes !== UnknownErrorMessage
                ) {
                    toast.error(errorMessageRes, arg)
                }

                setError(error)
                setFetchingStatus(FetchingStatus.ERROR)

                // in-case of refresh token was expired we don't need to throw the exception
                // The reason is in `httpRequest/index.ts`, we have already dispatched event `authExpiredHandler` which implicitly logout() and remove the credential
                // this will also prevent sending the unhandled promise with HTTP 401 error to Sentry
                if (errorMessageRes === CancelRefreshTokenExpired) {
                    console.log(
                        'fetch API was cancelled due to token/refresh token was expired.',
                    )
                    return
                }

                throw error
            }
        },
        [
            setBreadcrumb,
            apiFunc,
            config?.showErrorToast,
            config?.showSuccessToast,
            config?.breadcrumbName,
            config?.successMessage,
        ],
    )

    const resetStatus = useCallback(() => {
        setFetchingStatus(FetchingStatus.IDLE)
    }, [])
    const resetError = useCallback(() => {
        setError(undefined)
    }, [])

    return [
        response,
        callApi,
        statusFetching,
        error,
        resetStatus,
        setResponse,
        resetError,
    ] as const
}

export default useFetchApi
